import { useMoneyFormatter } from "@/lib/money-format";
/**
 * /companies/:companyId/reportes/estado-resultados
 * Estado de Resultados (P&L) view.
 */
import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Printer, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ProfitLossView } from "@/components/reports/ProfitLossView";
import { PrintableReport } from "@/components/reports/PrintableReport";
import { ReportPeriodPicker, type PeriodRange } from "@/components/reports/ReportPeriodPicker";
import { CsvExportButton } from "@/components/reports/CsvExportButton";
import { useActiveCompany } from "@/lib/company-context";
import { useProfitLoss } from "@/lib/api/reports.queries";

export const Route = createFileRoute("/companies/$companyId/reportes/estado-resultados")({
  component: EstadoResultadosPage,
});

function currentMonthRange(): PeriodRange {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
  return { from: `${y}-${m}-01`, to: `${y}-${m}-${String(lastDay).padStart(2, "0")}` };
}

function EstadoResultadosPage() {
  return (
    <RequireAuth>
      <AppShell>
        <EstadoResultadosContent />
      </AppShell>
    </RequireAuth>
  );
}

function EstadoResultadosContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const [period, setPeriod] = useState<PeriodRange>(currentMonthRange);

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const { data: report, isLoading, isError, refetch } = useProfitLoss(companyId, {
    periodFrom: period.from,
    periodTo: period.to,
  });

  // Flatten report data for CSV export
  const formatMoney = useMoneyFormatter("decimal");

  const csvRows = report
    ? [
        ...report.income.items.map((i) => ({
          seccion: "Ingresos",
          cuenta: i.accountCode,
          nombre: i.accountName,
          monto: formatMoney(i.amount),
        })),
        ...report.costOfSales.items.map((i) => ({
          seccion: "Costo de ventas",
          cuenta: i.accountCode,
          nombre: i.accountName,
          monto: formatMoney(i.amount),
        })),
        ...report.operatingExpenses.items.map((i) => ({
          seccion: "Gastos operacionales",
          cuenta: i.accountCode,
          nombre: i.accountName,
          monto: formatMoney(i.amount),
        })),
        ...report.otherIncome.items.map((i) => ({
          seccion: "Otros ingresos",
          cuenta: i.accountCode,
          nombre: i.accountName,
          monto: formatMoney(i.amount),
        })),
        ...report.otherExpense.items.map((i) => ({
          seccion: "Otros gastos",
          cuenta: i.accountCode,
          nombre: i.accountName,
          monto: formatMoney(i.amount),
        })),
      ]
    : [];

  return (
    <PrintableReport
      title={t("reports.pl.title")}
      subtitle={report ? `${report.period.from} — ${report.period.to}` : undefined}
    >
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-600" />
            <h1 className="text-xl font-bold">{t("reports.pl.title")}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            <CsvExportButton
              filename={`estado-resultados-${period.from}-${period.to}`}
              rows={csvRows}
              headers={[
                { key: "seccion", label: "Seccion" },
                { key: "cuenta", label: "Codigo" },
                { key: "nombre", label: "Nombre" },
                { key: "monto", label: "Monto COP" },
              ]}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="print:hidden"
              onClick={() => window.print()}
            >
              <Printer className="mr-2 h-4 w-4" />
              {t("reports.print")}
            </Button>
          </div>
        </div>

        {/* Period picker */}
        <div className="print:hidden">
          <ReportPeriodPicker value={period} onChange={setPeriod} />
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-48 rounded-lg" />
            <Skeleton className="h-48 rounded-lg" />
          </div>
        ) : isError ? (
          <ErrorState
            title={t("reports.error.title")}
            description={t("reports.error.body")}
            onRetry={() => void refetch()}
          />
        ) : report ? (
          <ProfitLossView report={report} />
        ) : null}
      </div>
    </PrintableReport>
  );
}
